import type { DealFormData, DealServiceFormData } from '../types';

/**
 * Persistenza bozza di un'offerta in localStorage.
 *
 * Il DealForm autosaslva l'intero stato di lavoro mentre l'utente compila,
 * così se chiude il browser/tab/refreshia non perde nulla. Una sola chiave
 * globale `deal_draft` (non per-utente perché lo storage è già locale al
 * browser dell'utente).
 *
 * NOTA: i timestamp `lastUpdated` / `singleInvoiceDate` sono serializzati
 * come stringa ISO (Date non sopravvive a JSON.stringify in modo
 * idempotente). Il loader li lascia stringa: chi consuma deve fare
 * `new Date(...)` quando serve un Date.
 */

export const DEAL_DRAFT_KEY = 'deal_draft';

export interface DealDraftExtra {
  currentStep?: number;
  activeCommissionTab?: 'deal' | 'commissions' | 'referrers' | 'billing';
  dealReferrerIds?: string[];
  singleInvoiceMode?: boolean;
  singleInvoiceDate?: Date | null;
  overrideCommissionScaling?: boolean | null;
  selectedContactIds?: string[];
}

export interface DealDraft extends Omit<DealDraftExtra, 'singleInvoiceDate'> {
  formData: DealFormData;
  serviceItems: DealServiceFormData[];
  singleInvoiceDate?: string; // ISO, da convertire a Date al load
  lastUpdated: string;
}

export function loadDealDraft(): DealDraft | null {
  try {
    const raw = localStorage.getItem(DEAL_DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw) as DealDraft;
    return draft;
  } catch (error) {
    console.error('❌ Error loading deal draft from localStorage:', error);
    return null;
  }
}

export function saveDealDraft(
  formData: DealFormData,
  serviceItems: DealServiceFormData[],
  extra: DealDraftExtra = {}
): void {
  const draft: DealDraft = {
    formData,
    serviceItems,
    currentStep: extra.currentStep,
    activeCommissionTab: extra.activeCommissionTab,
    dealReferrerIds: extra.dealReferrerIds,
    singleInvoiceMode: extra.singleInvoiceMode,
    singleInvoiceDate: extra.singleInvoiceDate?.toISOString(),
    overrideCommissionScaling: extra.overrideCommissionScaling,
    selectedContactIds: extra.selectedContactIds,
    lastUpdated: new Date().toISOString(),
  };
  localStorage.setItem(DEAL_DRAFT_KEY, JSON.stringify(draft));
}

export function clearDealDraft(): void {
  localStorage.removeItem(DEAL_DRAFT_KEY);
}
